<template>
  <div class="job-architecture-list-view">
    <div
      class="job-architecture-table-header architecture-row-grid"
      :class="{ 'edit-mode': isEditMode, standalone: isStandaloneJobNav }"
    >
      <div class="text-body-2 text-textDarkest">
        Name
      </div>
      <div class="text-body-2 text-textDarkest">
        Job Families
      </div>
      <div class="text-body-2 text-textDarkest">
        Job Profiles
      </div>
      <div
        v-if="!isStandaloneJobNav"
        class="text-body-2 text-textDarkest"
      >
        Employees
      </div>
      <div
        v-if="isEditMode"
        class="text-body-2 text-textDarkest"
      >
        Actions
      </div>
      <div
        v-else
        class="text-body-2 text-textDarkest architecture-header-alerts d-flex align-center ga-1"
      >
        Alerts
        <div class="d-flex align-center ml-1" @click.stop>
          <InsightsTooltip :tooltip="alertsTooltip" tooltip-placement="right" />
        </div>
      </div>
    </div>
    <div>
      <JobArchitectureAddRow
        v-if="showAddControls"
        variant="group"
      />
      <ListFilterEmptyState
        v-if="showEmptyState"
        hide-bottom-border
        message="No items match your filters or search. Try adjusting your filters or search term."
      />
      <JobArchitectureTreeview
        v-else
        v-model:selected="selectedProfiles"
        :items="treeItems"
        item-value="value"
        item-title="title"
        item-children="children"
        selectable
      >
        <template #item-label="{ item }">
          <JobArchitectureAddRow
            v-if="item.nodeType === 'add-family'"
            variant="family"
            :group-ulid="item.groupUlid"
          />
          <template v-else>
            {{ item.title }}
          </template>
        </template>
        <template
          v-if="isEditMode"
          #item-actions="{ item, isSelected, toggleSelection }"
        >
          <JobArchitectureRowActions
            :item="item"
            :all-selected="isSelected"
            @toggle-selection="toggleSelection"
          />
        </template>
      </JobArchitectureTreeview>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue';
import { storeToRefs } from 'pinia';
import {
  computeArchitectureAlerts,
  familyMapKey,
  getFamilyAlertTooltipContext,
  getGroupAlertTooltipContext,
  getProfileAlertTooltipContext,
} from '@/components/jobArchitecture/alertHelpers';
import {
  groupId,
  familyNodeId,
  getGroupArchitectureStats,
  getGroupTotalProfileCount,
  getFamilyArchitectureStats,
  formatArchitectureFamilyCountLabel,
  formatArchitectureProfilesCountLabel,
  groupArchitectureFamilyProfilesForDisplay,
} from '@/components/jobArchitecture/helpers';
import JobArchitectureTreeview from '@/components/shared/JobArchitectureTreeview.vue';
import JobArchitectureRowActions from '@/components/jobArchitecture/JobArchitectureRowActions.vue';
import JobArchitectureAddRow from '@/components/jobArchitecture/JobArchitectureAddRow.vue';
import ListFilterEmptyState from '@/components/shared/ListFilterEmptyState.vue';
import InsightsTooltip from '@/components/insights/InsightsTooltip.vue';
import { getTooltip } from '@/helpers/tooltips';
import { useAppAuthData } from '@/stores/app/authData';
import { useAppLoading } from '@/stores/app/loading';
import { useFilters } from '@/stores/filters/filters';
import { useJobArchitectureFilters } from '@/composables/useJobArchitectureFilters';
import { useJobArchitecture } from '@/stores/jobArchitecture/jobArchitecture';
import { formatJobLevel } from '@/helpers/jobLevelDisplay';

const props = defineProps({
  treeData: { type: Object, default: null },
  canEdit: {
    type: Boolean,
    default: false,
  },
});

const selectedProfiles = defineModel('selectedProfiles', {
  type: Array,
  default: () => [],
});

const { pageLoading } = storeToRefs(useAppLoading());
const { isStandaloneJobNav } = storeToRefs(useAppAuthData());
const { areNonIgnorableFiltersApplied } = storeToRefs(useFilters());
const { searchText } = useJobArchitectureFilters();
const { isEditMode } = storeToRefs(useJobArchitecture());

const showAddControls = computed(() => props.canEdit && isEditMode.value);

const alertsTooltip = computed(() => getTooltip('architecture-alerts'));

const alertOptions = computed(() => ({
  skipEmployeeAlerts: isStandaloneJobNav.value,
}));

const alertsData = computed(() => computeArchitectureAlerts(props.treeData, alertOptions.value));

const treeItems = computed(() => {
  const groups = props.treeData?.groups;

  if (!groups?.length) return [];

  const {
    byProfile,
    byFamily,
    byGroup,
    profileCounts,
    familyCounts,
  } = alertsData.value;
  const profileCountMap = profileCounts;
  const familyCountMap = familyCounts;

  return groups.map((group) => {
    const families = group.families || [];
    const groupStats = getGroupArchitectureStats(group);
    const profileTotal = getGroupTotalProfileCount(group);
    const groupNodeIdValue = groupId(group);

    return {
      title: group.name || 'Uncategorized',
      value: groupNodeIdValue,
      ulid: group.ulid,
      name: group.name,
      structureFamiliesLabel: formatArchitectureFamilyCountLabel(groupStats.familyCount),
      structureProfilesLabel: formatArchitectureProfilesCountLabel(profileTotal),
      employeesDisplay: groupStats.employeeCount,
      alerts: byGroup.get(groupNodeIdValue) ?? [],
      nodeType: 'group',
      alertTooltipContext: getGroupAlertTooltipContext(),
      children: [
        ...families.map((family) => {
          const {
            published,
            unconnectedDrafts,
          } = groupArchitectureFamilyProfilesForDisplay(family.profiles);
          const famStats = getFamilyArchitectureStats(family);
          const profileLabel = `${famStats.profileCount} job profile${famStats.profileCount === 1 ? '' : 's'}`;
          const familyKey = familyMapKey(group, family);

          const buildProfileTreeItem = (profile, { isDraftRow = false } = {}) => ({
            title: profile.title || '',
            value: profile.ulid,
            ulid: profile.ulid,
            familyUlid: family.ulid,
            employeesDisplay: profile.employee_count || 0,
            jobLevel: formatJobLevel(profile.job_level, { mode: 'short' }) || null,
            jobCode: profile.hris_job_code,
            isDraftRow,
            hideCheckbox: false,
            alerts: profile.ulid ? (byProfile.get(profile.ulid) ?? []) : [],
            nodeType: 'profile',
            alertTooltipContext: profile.ulid
              ? getProfileAlertTooltipContext(profileCountMap, profile.ulid)
              : { familyNames: [], groupNames: [] },
          });

          return {
            title: family.name || 'Uncategorized',
            value: familyNodeId(group, family),
            ulid: family.ulid,
            name: family.name,
            groupUlid: group.ulid,
            profileCount: famStats.profileCount,
            draftCount: family.draft_count ?? null,
            structureProfilesLabel: profileLabel,
            employeesDisplay: famStats.employeeCount,
            alerts: byFamily.get(familyKey) ?? [],
            nodeType: 'family',
            hideCheckbox: isEditMode.value,
            alertTooltipContext: getFamilyAlertTooltipContext(familyCountMap, familyKey),
            children: [
              ...published.map((profile) => buildProfileTreeItem(profile)),
              ...unconnectedDrafts.map((profile) => buildProfileTreeItem(profile, {
                isDraftRow: true,
              })),
            ],
          };
        }),
        ...(showAddControls.value ? [{
          nodeType: 'add-family',
          value: `add-family-${group.ulid}`,
          groupUlid: group.ulid,
          hideCheckbox: true,
          title: '',
        }] : []),
      ],
    };
  });
});

const hasFiltersOrSearch = computed(() => (
  Boolean(searchText.value) || areNonIgnorableFiltersApplied.value
));

const showEmptyState = computed(() => (
  !pageLoading.value
  && treeItems.value.length === 0
  && hasFiltersOrSearch.value
));
</script>

<style scoped lang="less">
.job-architecture-list-view {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.architecture-row-grid {
  display: grid;
  align-items: center;
  grid-template-columns: minmax(0, 1fr) minmax(70px, 105px) minmax(80px, 120px) 88px minmax(160px, 185px);
  column-gap: 12px;
  padding-left: 43px;
  padding-right: 16px;

  &.standalone {
    grid-template-columns: minmax(0, 1fr) minmax(70px, 105px) minmax(80px, 120px) minmax(160px, 185px);
  }

  &.edit-mode {
    grid-template-columns: minmax(0, 1fr) minmax(70px, 105px) minmax(80px, 120px) 88px minmax(160px, min(340px, 33%));

    &.standalone {
      grid-template-columns: minmax(0, 1fr) minmax(70px, 105px) minmax(80px, 120px) minmax(160px, min(340px, 45%));
    }
  }
}

.job-architecture-table-header {
  position: sticky;
  top: 96px;
  z-index: 2;
  height: 40px;
  border-bottom: 1px solid @stroke-default;
  border-top: 1px solid @stroke-default;
  background-color: @bg-canvas;
  border-radius: 8px 8px 0 0;
}
.architecture-header-alerts {
  width: 185px;
  min-width: 160px;
  padding-left: 4px;
}
</style>
